/**
 * Small presentational parts shared by the case-approvals detail panel,
 * contract/payment sections, and review modal. All theme-driven (no hex).
 */
import { Platform, Pressable, Text, View, type TextStyle } from "react-native";

import { formatCents } from "../../../lib/caseApprovals";
import type { CompetitionTier, PricingTier } from "../../../types/database";
import { RADII, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../brand/paneThemes";

const THEME = PANE_THEMES.Admin;

export function SubLabel({ children, style }: { children: string; style?: TextStyle }) {
  return (
    <Text
      style={[
        {
          marginTop: 6,
          fontFamily: THEME.bodyBoldFont,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: THEME.textOnSurfaceDim,
        },
        style ?? null,
      ]}
    >
      {children}
    </Text>
  );
}

export function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <View style={{ flexDirection: "row", marginTop: 4, gap: 8 }}>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim, minWidth: 120 }}>{k}</Text>
      <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurface }}>{v}</Text>
    </View>
  );
}

export function PreviewRow({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <View style={{ flexDirection: "row", justifyContent: "space-between", gap: 8 }}>
      <Text style={{ fontFamily: strong ? THEME.bodyBoldFont : THEME.bodyFont, fontSize: strong ? 12.5 : 12, color: strong ? THEME.textOnSurface : THEME.textOnSurfaceDim }}>
        {k}
      </Text>
      <Text style={{ fontFamily: strong ? THEME.bodyBoldFont : THEME.bodyMediumFont, fontSize: strong ? 13 : 12, color: strong ? THEME.accent : THEME.textOnSurface }}>
        {v}
      </Text>
    </View>
  );
}

export function tierRateSummary(row: PricingTier): string {
  const parts: string[] = [];
  if (row.flat_rm_fee_cents != null) parts.push(`${formatCents(row.flat_rm_fee_cents)} flat`);
  parts.push(`${formatCents(row.per_student_rate_cents)}/student`);
  parts.push(`${formatCents(row.per_submission_rate_cents)}/submission`);
  if (row.per_presentation_rate_cents != null) {
    parts.push(`${formatCents(row.per_presentation_rate_cents)}/team presentation`);
  }
  return parts.join(" · ");
}

/**
 * Radio-style tier picker. LIC shows all 4 tiers (100/250/500/unlimited);
 * RM hides "unlimited" and adds the flat fee + per-presentation rate. Hybrid
 * has no pricing rows, so it renders an explainer.
 */
export function TierPicker({
  engagement,
  pricing,
  selected,
  onSelect,
}: {
  engagement: "licensing" | "rm" | "hybrid";
  pricing: PricingTier[];
  selected: CompetitionTier | null;
  onSelect: (tier: CompetitionTier) => void;
}) {
  if (engagement === "hybrid") {
    return (
      <View
        style={{
          padding: 12,
          borderRadius: RADII.sm,
          borderWidth: STROKES.hairline,
          borderColor: THEME.destructive,
          backgroundColor: THEME.altBg,
        }}
      >
        <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.destructive }}>
          Hybrid engagement has no pricing rows. Switch to RM or Licensing before picking a tier.
        </Text>
      </View>
    );
  }
  const allTiers: CompetitionTier[] =
    engagement === "rm" ? ["100", "250", "500"] : ["100", "250", "500", "unlimited"];
  return (
    <View style={{ borderRadius: RADII.sm, borderWidth: STROKES.hairline, borderColor: THEME.border, backgroundColor: THEME.surfaceRaisedBg, overflow: "hidden" }}>
      {allTiers.map((tier, idx) => {
        const row = pricing.find((p) => p.tier === tier && p.engagement_model === engagement);
        const isSelected = selected === tier;
        return (
          <Pressable
            key={tier}
            onPress={() => onSelect(tier)}
            style={[
              {
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderTopWidth: idx === 0 ? 0 : STROKES.hairline,
                borderTopColor: THEME.border,
                backgroundColor: isSelected ? THEME.accentSoft : "transparent",
              },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <View
              style={{
                width: 14,
                height: 14,
                borderRadius: RADII.full,
                borderWidth: STROKES.hairline,
                borderColor: isSelected ? THEME.accent : THEME.textOnSurfaceFaint,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected ? <View style={{ width: 6, height: 6, borderRadius: RADII.full, backgroundColor: THEME.accent }} /> : null}
            </View>
            <Text style={{ fontFamily: THEME.bodySemiBoldFont, fontSize: 12.5, color: isSelected ? THEME.accent : THEME.textOnSurface, minWidth: 96 }}>
              {tier === "unlimited" ? "Unlimited" : `${tier} students`}
            </Text>
            <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: isSelected ? THEME.textOnSurfaceDim : THEME.textOnSurfaceFaint }}>
              {row ? tierRateSummary(row) : "Pricing not loaded"}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
